/**
 * project level usage analytics
 * https://mixpanel.com/api/app/projects/${projectId}/usage/data_queries/
 *
 * organization level usage analytics
 * https://mixpanel.com/organizations/${orgId}/usage/data_queries/
 */
import {apiClient} from 'common/util/api-client';
import {FetchOptions} from 'common/util/shared-client';

import {appApiUrl} from '../utils';
import {
  ChartDataEndpointPath,
  ChartDateRange,
  Math,
  UsageAnalyticsApiResponse,
  ValidChartDateRanges,
} from './usage-analytics-client-types';

class UsageAnalyticsClient {
  buildOrgEndpoint(orgId: number, endpointName: ChartDataEndpointPath) {
    return `/organizations/${orgId}/usage/${endpointName}`;
  }

  buildProjectEndpoint(projectId: number, endpointName: ChartDataEndpointPath, distinctId) {
    return appApiUrl(`usage/${endpointName}/${distinctId ?? ``}`, {projectId});
  }

  async getChartData({
    usageContext,
    id,
    chartEndpoint,
    chartDateRange = ChartDateRange.Weeks12,
    math,
    distinctId,
    queryLimit,
    fetchOptions = {},
  }: {
    usageContext: string;
    id: number;
    chartEndpoint: ChartDataEndpointPath;
    chartDateRange?: ChartDateRange;
    fetchOptions?: FetchOptions;
    math?: Math;
    distinctId?: string;
    queryLimit?: number;
  }) {
    let response = null;
    if (!ValidChartDateRanges.includes(chartDateRange)) {
      console.error(`invalid chart date range. please use ${ValidChartDateRanges.join(`, `)}`);
      return response;
    }
    const queryParams = {
      date_range: chartDateRange,
      ...(math ? {math} : {}),
      ...(queryLimit ? {query_limit: queryLimit} : {}),
    };
    if (usageContext === `organization`) {
      response = await apiClient.get<UsageAnalyticsApiResponse>(
        this.buildOrgEndpoint(id, chartEndpoint),
        queryParams,
        fetchOptions,
      );
    } else if (usageContext === `project`) {
      response = await apiClient.get<UsageAnalyticsApiResponse>(
        this.buildProjectEndpoint(id, chartEndpoint, distinctId),
        queryParams,
        fetchOptions,
      );
    } else {
      console.error(`invalid usage context. please use either organization or project`);
    }
    return response;
  }
}

export const usageAnalyticsClient = new UsageAnalyticsClient();
